import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import moment from "moment";
import { useDispatch } from "react-redux";
import CameraCapture from "../../../components/shared/CameraCapture";
import { toast } from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Upload,
  Save,
  Camera,
  CheckCircle,
  AlertCircle,
  Home,
  Shield,
  Wind,
  Droplets,
  Flame,
  Sparkles,
  Package,
  Heart,
  Users,
  CalendarDays,
  Toilet,
  Trash2,
  Droplet,
  Gauge,
  Bug,
  Calendar,
  SprayCan,
  FileText,
} from "lucide-react";

const SectionFSanitation = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    f3Document: null,
    f4Photo: null,
    f7Photo: null,
    f8Document: null,
  });

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const [form, setForm] = useState({
    f1: "",
    f2: "",
    f3: "",
    f3Document: null,
    f4: "",
    f4Photo: null,
    f5: "",
    f6: "",
    f7: "",
    f7Photo: null,
    f8: "",
    f8Date: "",
    f8Document: null,
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_f");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          const { timestamp, canteenInfo, ...formData } = savedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_f", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size immediately
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && file.type.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: imageUrl,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.f1) newErrors.f1 = t.global.err_a1;
    if (!form.f2) newErrors.f2 = t.global.err_a1;
    if (!form.f3) newErrors.f3 = t.global.err_a1;
    if (!form.f4) newErrors.f4 = t.global.err_a1;
    if (!form.f5) newErrors.f5 = t.global.err_a1;
    if (!form.f6) newErrors.f6 = t.global.err_a1;
    if (!form.f7) newErrors.f7 = t.global.err_a1;
    if (!form.f8) newErrors.f8 = t.global.err_a1;

    // File validation only if file IS uploaded
    if (form.f3Document && form.f3Document.size > 5 * 1024 * 1024) {
      newErrors.f3Document = t.global.err_file_size;
    }
    if (form.f3Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.f3Document.type)) {
        newErrors.f3Document = t.global.err_file_type_long;
      }
    }

    if (form.f4Photo && form.f4Photo.size > 5 * 1024 * 1024) {
      newErrors.f4Photo = t.global.err_file_size;
    }
    if (form.f4Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.f4Photo.type)) {
        newErrors.f4Photo = t.global.err_file_type_long;
      }
    }

    if (form.f7Photo && form.f7Photo.size > 5 * 1024 * 1024) {
      newErrors.f7Photo = t.global.err_file_size;
    }
    if (form.f7Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.f7Photo.type)) {
        newErrors.f7Photo = t.global.err_file_type_long;
      }
    }

    if (form.f8Document && form.f8Document.size > 5 * 1024 * 1024) {
      newErrors.f8Document = t.global.err_file_size;
    }
    if (form.f8Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.f8Document.type)) {
        newErrors.f8Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isPartial =
      !form.f1 ||
      !form.f2 ||
      !form.f3 ||
      !form.f4 ||
      !form.f5 ||
      !form.f6 ||
      !form.f7 ||
      !form.f8;

    setIsSubmitting(true);

    try {
      const fd = new FormData();

      // =====================
      // Location & Master IDs
      // =====================
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // =====================
      // Section F (Sanitation)
      // =====================
      fd.append("sanitation_F1", yesNoToInt(form.f1));
      fd.append("sanitation_F2", yesNoToInt(form.f2));
      fd.append("sanitation_F3", yesNoToInt(form.f3));
      if (form.f3 === t.global.yes && form.f3Document) {
        fd.append("sanitation_F3_upload", form.f3Document);
      }

      fd.append("sanitation_F4", yesNoToInt(form.f4));
      if (form.f4 === t.global.yes && form.f4Photo) {
        fd.append("sanitation_F4_upload", form.f4Photo);
      }

      fd.append("sanitation_F5", yesNoToInt(form.f5));
      fd.append("sanitation_F6", yesNoToInt(form.f6));

      fd.append("sanitation_F7", yesNoToInt(form.f7));
      if (form.f7 === t.global.yes && form.f7Photo) {
        fd.append("sanitation_F7_upload", form.f7Photo);
      }

      fd.append("sanitation_F8", yesNoToInt(form.f8));
      if (form.f8 === t.global.yes) {
        if (form.f8Date) {
          fd.append("sanitation_F8_date", form.f8Date);
        }
        if (form.f8Document) {
          fd.append("sanitation_F8_upload", form.f8Document);
        }
      }

      fd.append("current_section", "sanitation");
      fd.append("reporting_date", moment().format("YYYY-MM-DD"));

      // =====================
      // Dispatch API
      // =====================
      await dispatch(createKitchen(fd));

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_f", JSON.stringify(sectionData));

      // ✅ Mark Section F as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.f = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_f", JSON.stringify(sectionData));

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section F submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.F.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.F.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center">
                <Droplet size={14} className="mr-1" />
                Sanitation Survey
              </div>
              <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* F.1 - Toilet Functionality */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Toilet size={18} className="mr-2" />
                F.1 {t.modules.kitchen.sections.F.questions.staffToilet}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchen.sections.F.hints.staffToilet.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Running water for flushing and cleaning</li>
                  <li>Non-choking toilet pans and urinals</li>
                  <li>Functional cisterns</li>
                  <li>Washbasin with soap and water for handwash</li>
                  <li>Proper drainage system</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f1"
                      value={option}
                      checked={form.f1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f1}
                </p>
              )}
            </div>

            {/* F.2 - Toilet Condition */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Wind size={18} className="mr-2" />
                F.2 {t.modules.kitchen.sections.F.questions.toiletConditions}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f2"
                      value={option}
                      checked={form.f2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f2}
                </p>
              )}
            </div>

            {/* F.3 - Toilet Cleaning Records */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Sparkles size={18} className="mr-2" />
                F.3 {t.modules.kitchen.sections.F.questions.toiletCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.F.hints.toiletCleaning}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f3"
                      value={option}
                      checked={form.f3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f3}
                </p>
              )}

              {form.f3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-medium mb-4 text-gray-700 flex items-center">
                    <FileText size={14} className="mr-2" />
                    Upload current month's housekeeping record (Optional but recommended):
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("f3Document");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="f3Document"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="f3Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    className="hidden"
                    id="f3Document"
                  />

                  {/* PREVIEW */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.f3Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.f3Document ? (
                      <div className="space-y-3">
                        {previewImages.f3Document && (
                          <img
                            src={previewImages.f3Document}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.f3Document.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, f3Document: null }));
                              setPreviewImages((prev) => ({ ...prev, f3Document: null }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        Attach current month's housekeeping record
                      </p>
                    )}
                  </div>

                  {errors.f3Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />{" "}
                      {errors.f3Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* F.4 - Dustbin in Toilet */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Trash2 size={18} className="mr-2" />
                F.4 {t.modules.kitchen.sections.F.questions.sanitaryBin}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f4 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f4"
                      value={option}
                      checked={form.f4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f4 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f4}
                </p>
              )}

              {form.f4 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Upload or capture a picture of the dustbin (Optional but recommended):
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("f4Photo");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointerfont-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="f4Photo"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="f4Photo"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                    id="f4Photo"
                  />

                  {/* PREVIEW */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.f4Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.f4Photo ? (
                      <div className="space-y-3">
                        {previewImages.f4Photo && (
                          <img
                            src={previewImages.f4Photo}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.f4Photo.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, f4Photo: null }));
                              setPreviewImages((prev) => ({ ...prev, f4Photo: null }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        Photo evidence of dustbin (if available)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* F.5 - Drainage System */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Gauge size={18} className="mr-2" />
                F.5 {t.modules.kitchen.sections.F.questions.coveredDrainage}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f5 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f5 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f5"
                      value={option}
                      checked={form.f5 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f5 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f5 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f5}
                </p>
              )}
            </div>

            {/* F.6 - Cleaning Materials Storage */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Package size={18} className="mr-2" />
                F.6 {t.modules.kitchen.sections.F.questions.cleaningMaterials}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f6 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f6 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f6"
                      value={option}
                      checked={form.f6 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f6 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f6 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f6}
                </p>
              )}
            </div>

            {/* F.7 - 2-Bucket System */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <SprayCan size={18} className="mr-2" />
                F.7 {t.modules.kitchen.sections.F.questions.twoBucketSystem}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f7 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f7 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f7"
                      value={option}
                      checked={form.f7 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f7 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f7 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f7}
                </p>
              )}

              {form.f7 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Upload or capture a picture of 2-bucket system for floor mopping (Optional but recommended):
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("f7Photo");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="f7Photo"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  {/* HIDDEN FILE INPUT */}
                  <input
                    type="file"
                    name="f7Photo"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                    id="f7Photo"
                  />

                  {/* PREVIEW */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.f7Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.f7Photo ? (
                      <div className="space-y-3">
                        {previewImages.f7Photo && (
                          <img
                            src={previewImages.f7Photo}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.f7Photo.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, f7Photo: null }));
                              setPreviewImages((prev) => ({ ...prev, f7Photo: null }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        Photo evidence of 2-bucket system (if available)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* F.8 - Pest Control */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Bug size={18} className="mr-2" />
                F.8 {t.modules.kitchen.sections.F.questions.pestControl}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.F.hints.pestControl}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.f8 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.f8 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="f8"
                      value={option}
                      checked={form.f8 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.f8 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.f8 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.f8}
                </p>
              )}

              {form.f8 === t.global.yes && (
                <div className="mt-6 space-y-6 p-4 bg-green-50 border border-green-300 rounded-lg">
                  {/* DATE FIELD */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 flex items-center">
                      <Calendar size={14} className="mr-2" />
                      Last date of pest control activity (Optional but recommended):
                    </label>

                    <input
                      type="date"
                      name="f8Date"
                      value={form.f8Date}
                      onChange={handleChange}
                      max={new Date().toISOString().split("T")[0]}
                      className={`w-full p-3 rounded-lg border-2 transition-all duration-150 ease-out focus:outline-none ${
                        errors.f8Date
                          ? "border-red-400 bg-red-50"
                          : "border-gray-300 bg-gray-50 focus:border-blue-500 focus:bg-white"
                      }`}
                    />

                    {errors.f8Date && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.f8Date}
                      </p>
                    )}
                  </div>

                  {/* DOCUMENT SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Attach pest control record (Optional but recommended):
                    </label>

                    {/* SELECTION BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("f8Document");
                          setShowCamera(true);
                        }}
                        className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium"
                      >
                        <Camera size={20} /> {t.global.useCamera}
                      </button>

                      <label
                        htmlFor="f8Document"
                        className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                      >
                        <Upload size={20} /> {t.global.uploadPhoto}
                      </label>
                    </div>

                    {/* HIDDEN FILE INPUT */}
                    <input
                      type="file"
                      name="f8Document"
                      accept=".pdf,.jpg,.jpeg,.png,image/*"
                      onChange={handleChange}
                      className="hidden"
                      id="f8Document"
                    />

                    {/* PREVIEW */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                        form.f8Document
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.f8Document ? (
                        <div className="space-y-3">
                          {previewImages.f8Document && (
                            <img
                              src={previewImages.f8Document}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                          <div className="flex items-center justify-between">
                            <p className="text-gray-700 font-medium text-sm break-all">
                              {form.f8Document.name}
                            </p>

                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({ ...prev, f8Document: null }));
                                setPreviewImages((prev) => ({ ...prev, f8Document: null }));
                              }}
                              className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          Pest control record (if available)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Optional Fields Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Note:</span> All photo and
                    document uploads are{" "}
                    <span className="font-medium">optional</span>. However, if
                    you answer "YES" to questions requiring uploads (F.3, F.4,
                    F.7, F.8) and don't provide them, this section will be
                    marked as{" "}
                    <span className="font-medium">"Partially Filled"</span>.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    For pest control (F.8), both date and document are
                    recommended for complete compliance.
                  </p>
                </div>
              </div>
            </div>

            {/* Sanitation Importance Notice */}
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800 font-medium">
                    🧼 Sanitation & Hygiene Notice
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Proper sanitation is critical for food safety. Regular
                    cleaning, pest control, and proper waste management prevent
                    contamination and ensure a healthy environment for food
                    preparation.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for
                  comprehensive sanitation assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_f}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            const imageUrl = URL.createObjectURL(file);

            setPreviewImages((prev) => ({
              ...prev,
              [cameraField]: imageUrl,
            }));

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionFSanitation;